import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

const VECTOR = /^data:image\/svg\+xml|\.svg(?:[?#]|$)/i;

/**
 * How a loaded picture sits in `box` at its own size (an image pixel per CSS pixel):
 * - `covers`: at least as large on both sides, so `object-cover` only crops it, never enlarges it;
 * - `whole`: smaller on a side, shown whole and never enlarged, the box's ground beside it.
 *
 * `undefined` until `measure` is called with the loaded `<img>`. Follows the box's resizes. A
 * vector (SVG) always covers: it grows without blurring.
 */
export type PictureFit = 'covers' | 'whole';

type Size = { width: number; height: number };

/** The media dialog's corners (`rounded-xl`): a picture well clear of the edges gets these. */
const MAX_RADIUS_PX = 12;

export function fitOf(picture: Size, box: Size): PictureFit {
  return picture.width >= box.width && picture.height >= box.height
    ? 'covers'
    : 'whole';
}

/**
 * The corners of a picture shown whole: as round as the ground it leaves on its closest side, up
 * to the dialog's. One reaching an edge (a padded or filling one) stays square; one floating in
 * the middle rounds as in the dialog; in between it rounds as far as it can.
 */
export function cornerRadiusOf(picture: Size, box: Size): number {
  if (fitOf(picture, box) === 'covers') return 0;
  // one shrunk to fit reaches a pair of edges: no ground there, square corners
  const gap = Math.min(
    (box.width - picture.width) / 2,
    (box.height - picture.height) / 2
  );
  return Math.min(Math.max(gap, 0), MAX_RADIUS_PX);
}

/** The `PictureFit` and corner radius of the picture `measure` is given, in `box`. */
export function usePictureFit(box: RefObject<HTMLElement | null>) {
  const natural = useRef<{ width: number; height: number } | null>(null);
  const [state, setState] = useState<{ fit: PictureFit; radius: number }>();
  const compare = useCallback(() => {
    const element = box.current;
    if (!element || !natural.current) return;
    const size = { width: element.clientWidth, height: element.clientHeight };
    const fit = fitOf(natural.current, size);
    const radius = cornerRadiusOf(natural.current, size);
    // the same object while nothing changed: a resize drag doesn't re-render the picture
    setState(current =>
      current?.fit === fit && current.radius === radius
        ? current
        : { fit, radius }
    );
  }, [box]);
  useLayoutEffect(() => {
    const element = box.current;
    if (!element) return;
    const observer = new ResizeObserver(compare);
    observer.observe(element);
    return () => observer.disconnect();
  }, [box, compare]);
  /** Call from the picture's load, in the same handler that shows it: no frame stretched. */
  const measure = useCallback(
    (image: HTMLImageElement) => {
      natural.current =
        image.naturalWidth && !VECTOR.test(image.currentSrc)
          ? { width: image.naturalWidth, height: image.naturalHeight }
          : { width: Infinity, height: Infinity };
      compare();
    },
    [compare]
  );
  return { fit: state?.fit, radius: state?.radius ?? 0, measure };
}
